import React, { useCallback, useMemo, useState } from 'react'

import Dialog, {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FooterActionButton,
  FooterActions,
} from '../../Dialog'
import { Avatar } from '../../Avatar'
import { ClickableNonMailtoLink } from '../../helpers/ClickableLink'
import { PseudoListItemNoSearchResults } from '../../helpers/PseudoListItem'
import { BackendRemote } from '../../../backend-com'
import { selectedAccountId } from '../../../ScreenController'
import SettingsStoreInstance, {
  useSettingsStore,
} from '../../../stores/settings'
import useProcessQR, { SCAN_CONTEXT_TYPE } from '../../../hooks/useProcessQr'
import { useFetchGeneric } from '../../../hooks/useFetch'
import useTranslationFunction from '../../../hooks/useTranslationFunction'
import { getJsonFromBase64 } from '../../../utils/getJsonFromBase64'
import { colorFromString } from '../../../utils/colorFromString'
import { instanceHidesPublicSuggestions } from '../../../utils/slothfulInstanceConfig'
import { unknownErrorToString } from '@deltachat-desktop/shared/unknownErrorToString'
import { getLogger } from '@deltachat-desktop/shared/logger'

import type { DialogProps } from '../../../contexts/DialogContext'

import styles from './styles.module.scss'

const log = getLogger('renderer/dialogs/PublicBots')

const PUBLIC_BOTS_REPO_URL = 'https://github.com/deltachat-bot/public-bots'
const PUBLIC_BOTS_ISSUES_URL =
  'https://github.com/deltachat-bot/public-bots/issues'
const PUBLIC_BOTS_DATA_URL =
  'https://raw.githubusercontent.com/deltachat-bot/public-bots/main/frontend/data.json'

type PublicBotsJson = {
  bots: {
    description?: unknown
    lang?: unknown
    admin?: unknown
    url?: unknown
  }[]
  /** language code -> human readable name, e.g. `{ en: 'English' }` */
  langs?: { [key: string]: unknown }
}

interface BotEntry {
  name: string
  addr: string
  admin: string
  lang: string
  description: string
  url: string
}


function parseBotsJson(base64: string): {
  bots: BotEntry[]
  langs: { [key: string]: string }
} {
  const data = getJsonFromBase64(base64) as null | PublicBotsJson
  if (data == null || !Array.isArray(data.bots)) {
    throw new Error(`unexpected data format from ${PUBLIC_BOTS_DATA_URL}`)
  }
  const bots = data.bots
    // tolerate single malformed entries instead of failing the whole list
    .filter(bot => typeof bot === 'object' && bot != null)
    .map(bot => {
      const url = typeof bot.url === 'string' ? bot.url : ''
      const description =
        typeof bot.description === 'string' ? bot.description : ''
      const admin = typeof bot.admin === 'string' ? bot.admin : ''
      const lang = typeof bot.lang === 'string' ? bot.lang : ''
      // The invite link carries the bot's name and address in its
      // URL fragment, e.g. https://i.delta.chat/#FINGERPRINT&a=bot%40host&n=Bot
      let name = ''
      let addr = ''
      try {
        const params = new URLSearchParams(new URL(url).hash.slice(1))
        addr = params.get('a') ?? ''
        name = params.get('n') ?? ''
      } catch (error) {
        log.warn('failed to parse bot invite link', url, error)
      }
      // core marks a truncated name with a trailing '_' and renders it as
      // '…' (see decode_name in core's qr.rs) — mirror that so the list and
      // the confirmation dialog show the same name
      if (name.endsWith('_')) {
        name = name.slice(0, -1) + '…'
      }
      if (name === '') {
        name = addr !== '' ? addr : admin
      }
      return { url, description, admin, lang, name, addr }
    })
    .filter(bot => bot.name !== '' && /^https:\/\/i\.delta\.chat\//i.test(bot.url))

  const langs: { [key: string]: string } = {}
  if (typeof data.langs === 'object' && data.langs != null) {
    for (const [code, langName] of Object.entries(data.langs)) {
      if (typeof langName === 'string') {
        langs[code] = langName
      }
    }
  }
  return { bots, langs }
}

/**
 * Browsable directory of community-made public bots, reachable from the
 * "New Chat" dialog. The list is loaded from the third-party
 * deltachat-bot/public-bots repository, so the first use asks for consent.
 */
export default function PublicBotsDialog(props: DialogProps) {
  const { onClose } = props
  const settingsStore = useSettingsStore()[0]

  if (instanceHidesPublicSuggestions()) {
    // Nothing should open this dialog when the operator hid the community
    // suggestions instance-wide, but if something does: no consent screen,
    // no fetch.
    return null
  }

  return (
    <Dialog
      width={400}
      onClose={onClose}
      fixed
      dataTestid='public-bots-dialog'
      fullscreenOnMobile
    >
      {settingsStore == null ? (
        // Wait for the settings store: the consent state is unknown, and
        // accepting couldn't be stored yet anyway (the store's reducer
        // no-ops while its state is null).
        <DialogHeader title='Public Bots' onClose={onClose} />
      ) : settingsStore.desktopSettings.publicBotsRemoteLoadConsent === true ? (
        <PublicBotsList onClose={onClose} />
      ) : (
        <PublicBotsConsent onClose={onClose} />
      )}
    </Dialog>
  )
}

function PublicBotsConsent({ onClose }: { onClose: () => void }) {
  const tx = useTranslationFunction()

  const accept = () => {
    // Flipping the setting swaps this screen for the list right away
    // (the dialog re-renders on the settings store update).
    SettingsStoreInstance.effect.setDesktopSetting(
      'publicBotsRemoteLoadConsent',
      true
    )
  }

  return (
    <>
      <DialogHeader title='Public Bots' onClose={onClose} />
      <DialogBody>
        <DialogContent>
          <div className={styles.consentText}>
            <p>
              This is a <b>community-made</b> directory of public bots. The bots
              are run by volunteers — they are <b>not</b> checked, endorsed or
              operated by us, and we are not responsible for them or their
              content. Talk to them at your own discretion.
            </p>
            <p>
              The list is loaded from a third-party site:{' '}
              <ClickableNonMailtoLink href={PUBLIC_BOTS_REPO_URL}>
                github.com/deltachat-bot/public-bots
              </ClickableNonMailtoLink>
              . Continuing will fetch data from there.
            </p>
            <p>
              If you have an issue with one of the bots, please complain
              directly over there, in that project's{' '}
              <ClickableNonMailtoLink href={PUBLIC_BOTS_ISSUES_URL}>
                GitHub issues
              </ClickableNonMailtoLink>
              .
            </p>
          </div>
        </DialogContent>
      </DialogBody>
      <DialogFooter>
        <FooterActions align='spaceBetween'>
          <FooterActionButton onClick={onClose}>
            {tx('cancel')}
          </FooterActionButton>
          <FooterActionButton
            styling='primary'
            data-testid='public-bots-accept'
            onClick={accept}
          >
            Accept &amp; Load
          </FooterActionButton>
        </FooterActions>
      </DialogFooter>
    </>
  )
}

function PublicBotsList({ onClose }: { onClose: () => void }) {
  const tx = useTranslationFunction()
  const accountId = selectedAccountId()
  const processQr = useProcessQR()
  const [queryStr, setQueryStr] = useState('')

  const fetchBots = useCallback(async () => {
    // Through core's HTTP so this works the same on all runtimes
    // (in the browser edition it ends up as a plain `fetch()`).
    const response = await BackendRemote.rpc.getHttpResponse(
      accountId,
      PUBLIC_BOTS_DATA_URL
    )
    return parseBotsJson(response.blob)
  }, [accountId])
  const botsFetch = useFetchGeneric(fetchBots)

  const onBotClick = (bot: BotEntry) => {
    // Hand the invite link to the regular QR / invite-link machinery:
    // it asks for confirmation and then sets up the chat.
    processQr(accountId, bot.url, SCAN_CONTEXT_TYPE.DEFAULT)
    onClose()
  }

  return (
    <>
      <DialogHeader onClose={onClose}>
        <input
          data-no-drag-region
          className='search-input'
          onChange={e => setQueryStr(e.target.value)}
          value={queryStr}
          placeholder={tx('search')}
          aria-label={tx('search')}
          autoFocus
          spellCheck={false}
        />
      </DialogHeader>
      <DialogBody className={styles.publicBotsBody}>
        {botsFetch.loading ? (
          <div className={styles.listStatus}>{tx('loading')}</div>
        ) : botsFetch.result.ok !== true ? (
          <div className={styles.listStatus}>
            <p>
              {tx(
                'error_x',
                `Failed to fetch the bot list\n(from ${PUBLIC_BOTS_DATA_URL})\n` +
                  unknownErrorToString(botsFetch.result.err)
              )}
            </p>
            <FooterActionButton onClick={botsFetch.refresh}>
              Try again
            </FooterActionButton>
          </div>
        ) : (
          <BotList
            bots={botsFetch.result.value.bots}
            langs={botsFetch.result.value.langs}
            queryStr={queryStr}
            onBotClick={onBotClick}
          />
        )}
      </DialogBody>
      <DialogFooter>
        <div className={styles.footerNote}>
          Community-made list, not endorsed by us — report problems with a bot
          in the{' '}
          <ClickableNonMailtoLink href={PUBLIC_BOTS_ISSUES_URL}>
            GitHub issues
          </ClickableNonMailtoLink>{' '}
          of{' '}
          <ClickableNonMailtoLink href={PUBLIC_BOTS_REPO_URL}>
            deltachat-bot/public-bots
          </ClickableNonMailtoLink>
          .
        </div>
      </DialogFooter>
    </>
  )
}

function BotList({
  bots,
  langs,
  queryStr,
  onBotClick,
}: {
  bots: BotEntry[]
  langs: { [key: string]: string }
  queryStr: string
  onBotClick: (bot: BotEntry) => void
}) {
  const filteredBots = useMemo(() => {
    const query = queryStr.trim().toLowerCase()
    if (query === '') {
      return bots
    }
    return bots.filter(bot =>
      [
        bot.name,
        bot.admin,
        bot.description,
        bot.addr,
        langs[bot.lang] ?? bot.lang,
      ]
        .join('\n')
        .toLowerCase()
        .includes(query)
    )
  }, [bots, langs, queryStr])

  if (bots.length === 0) {
    return (
      <div className={styles.listStatus}>The directory is currently empty.</div>
    )
  }
  if (filteredBots.length === 0) {
    return <PseudoListItemNoSearchResults queryStr={queryStr} />
  }
  return (
    <ol className={styles.botList}>
      {filteredBots.map(bot => (
        <li key={bot.url} className='contact-list-item'>
          <button
            type='button'
            className={'contact-list-item-button ' + styles.botListItem}
            onClick={() => onBotClick(bot)}
          >
            <Avatar
              displayName={bot.name}
              addr={bot.addr}
              color={colorFromString(bot.name + bot.addr)}
              aria-hidden
            />
            <div className={styles.botInfo}>
              <div className={styles.botHeadline}>
                <span className={styles.botName}>{bot.name}</span>
                {bot.lang !== '' && (
                  <span className={styles.botLang}>
                    {langs[bot.lang] ?? bot.lang}
                  </span>
                )}
              </div>
              <div className={styles.botAdmin}>
                {bot.addr}
                {bot.admin !== '' && ` · by ${bot.admin}`}
              </div>
              <div className={styles.botDescription}>{bot.description}</div>
            </div>
          </button>
        </li>
      ))}
    </ol>
  )
}
